import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Clock, X } from 'lucide-react';

interface RfqExtendDeadlineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newClosingDate: string, reason: string) => Promise<void>;
  currentClosingDate: string;
  isSubmitting?: boolean;
}

export const RfqExtendDeadlineModal: React.FC<RfqExtendDeadlineModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentClosingDate,
  isSubmitting = false,
}) => {
  const [newDate, setNewDate] = useState('2026-09-20');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !isSubmitting) onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) return;
    await onConfirm(`${newDate}T17:00:00Z`, reason);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-heading">
      <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md animate-in fade-in" onClick={() => !isSubmitting && onClose()} />

      <div className="relative z-50 w-full max-w-md rounded-2xl border border-border bg-surface dark:bg-[#0e101f] text-foreground p-6 shadow-2xl space-y-4 animate-in zoom-in-95">
        <div className="flex items-start justify-between border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-primary/10 text-primary border border-primary/20">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-base text-foreground">Extend RFQ Submission Deadline</h3>
              <p className="text-xs text-foreground-secondary font-medium">Update supplier bid closing date and timezone.</p>
            </div>
          </div>

          <Button variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting} className="h-8 w-8 p-0 rounded-full">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div>
            <label className="font-bold text-foreground block mb-1">Current Closing Date</label>
            <input
              type="text"
              readOnly
              value={new Date(currentClosingDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              className="w-full rounded-xl border border-border bg-muted/40 px-3 py-2 font-mono font-bold text-foreground-secondary"
            />
          </div>

          <div>
            <label className="font-bold text-foreground block mb-1">New Closing Date (IST UTC+05:30) *</label>
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 font-mono font-bold text-foreground focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div>
            <label className="font-bold text-foreground block mb-1">Extension Reason *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Additional 5 days granted per vendor clarification request for OEM specs..."
              className="w-full rounded-xl border border-border bg-background p-3 font-medium text-foreground focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-border">
            <Button variant="outline" type="button" disabled={isSubmitting} onClick={onClose} className="rounded-xl font-bold">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !reason} className="rounded-xl font-bold bg-primary text-white">
              {isSubmitting ? 'Updating Deadline...' : 'Confirm Extension'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
